import { PropsWithChildren } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { Close } from '@styled-icons/material'
import { useClickOutsideElement, useIsMounted } from '@oneblink/apps-react'
export interface DialogProps {
  onClose?: () => void
  isOpen: boolean
  desktopContentWidth?: string
}

export const ModalHeading = styled.div`
  display: flex;
  margin: 0;
  padding-bottom: ${({ theme }) => theme.unit / 2}px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.nswPaletteGrey04};
`
ModalHeading.displayName = 'ModalHeading'

export const ModalHeadingText = styled.span`
  display: flex;
  flex-grow: 1;
  font-size: 1.6rem;
`
ModalHeadingText.displayName = 'ModalHeadingText'

type Props = PropsWithChildren<DialogProps>

export const CloseIcon = styled(Close)`
  display: flex;
  height: 2rem;
  padding: 0 1rem;
  cursor: pointer;
`
CloseIcon.displayName = 'CloseIcon'

const DialogContainer = styled.div<DialogProps>`
  ${({ isOpen }) => !isOpen && 'visibility: hidden;'}
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 4;
`
DialogContainer.displayName = 'DialogContainer'

const DialogContent = styled.div<{ contentWidth?: string }>`
  background-color: ${({ theme }) => theme.palette.nswPaletteOffWhite};
  width: ${({ contentWidth, theme }) => {
    return window.innerWidth <= theme.screenSizes.largeTablet
      ? '80%'
      : contentWidth
  }};
  max-height: calc(100vh - 15%);
  overflow-y: auto;
  @media (${({ theme }) => theme.breakPoints.phone}) {
    width: 100%;
  }
  display: flex;
  flex: 0 0 auto;
  border-radius: 4px;
  flex-direction: column;
  padding: ${({ theme }) => theme.unit}px;
`

DialogContent.displayName = 'DialogContent'

const DialogZIndexWrapper = styled.div`
  z-index: 4;
`
DialogZIndexWrapper.displayName = 'DialogZIndexWrapper'

export default function Dialog({
  children,
  onClose,
  desktopContentWidth: contentWidth,
  ...props
}: Props) {
  const contentRef = React.useRef(null)
  const isMounted = useIsMounted()
  const onClickOutside = React.useCallback(() => {
    if (onClose) {
      onClose()
    }
  }, [onClose])

  useClickOutsideElement(contentRef, onClickOutside)

  if (!isMounted.current) {
    return null
  }

  return (
    <DialogZIndexWrapper>
      <DialogContainer {...props}>
        <DialogContent ref={contentRef} contentWidth={contentWidth}>
          {children}
        </DialogContent>
      </DialogContainer>
    </DialogZIndexWrapper>
  )
}
